import { useEffect, useRef } from "react";

import {
  BackGuard,
  BackGuardEnv,
  createBackGuard,
  installLeavePopHub,
  leavesInApp,
  PopHub,
  PopHubHost,
  unloadPassed,
} from "@buildpy/core/lib/leave-guard";

interface Options {
  // 새로고침·창 닫기·앱 밖 이동에 브라우저 확인을 띄울지.
  unload: boolean;
  // 앱 안 링크(탭·사이드바) 누름에 확인을 띄울지.
  links: boolean;
  // 뒤로·앞으로 가기에 확인을 띄울지 — 취소하면 제자리로 되돌린다.
  back?: boolean;
  message: string;
}

type NavigationLike = { navigation?: { currentEntry?: { index: number } | null } };

// 실제 창의 기록 — 되돌리기 판단은 lib/leave-guard 의 createBackGuard 가 한다.
const windowHistory: BackGuardEnv = {
  href: () => window.location.href,
  state: () => window.history.state,
  replaceState: (state) => window.history.replaceState(state, ""),
  pushState: (state, url) => window.history.pushState(state, "", url),
  go: (delta) => window.history.go(delta),
  entryIndex: () => (window as Window & NavigationLike).navigation?.currentEntry?.index ?? null,
  confirm: (message) => window.confirm(message),
  redeliver: () => window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state })),
  timer: (fn, ms) => {
    const id = window.setTimeout(fn, ms);
    return () => window.clearTimeout(id);
  },
};

// 앱 시작 때 popstate 리스너를 한 번 건다 — 루트 레이아웃의 클라이언트 모듈(app/theme-provider) 최상위에서 불러 Next 라우터의 effect 보다 먼저 등록된다.
export const installLeaveGuard = (): PopHub | null =>
  typeof window === "undefined" ? null : installLeavePopHub(window as unknown as PopHubHost);

// 저장 안 한 편집을 두고 떠나려 하면 묻는다 — 링크·뒤로가기는 라우터보다 먼저(캡처 단계) 가로챈다.
export const useLeaveGuard = ({ unload, links, back = false, message }: Options): void => {
  const passRef = useRef<number | null>(null);

  useEffect(() => {
    if (!unload) return;
    const warn = (e: BeforeUnloadEvent) => {
      // 링크 확인에 방금 「예」 를 눌렀으면 두 번 묻지 않는다.
      if (unloadPassed(passRef.current, Date.now())) {
        passRef.current = null;
        return;
      }
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unload]);

  useEffect(() => {
    if (!links) return;
    const onClick = (e: MouseEvent) => {
      const link = e.target instanceof Element ? e.target.closest("a") : null;
      if (!(link instanceof HTMLAnchorElement)) return;
      const to = { href: link.href, target: link.target, download: link.hasAttribute("download") };
      if (!leavesInApp(e, to, window.location.href)) return;
      if (window.confirm(message)) {
        passRef.current = Date.now();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [links, message]);

  const backRef = useRef(back);
  const messageRef = useRef(message);
  const guardRef = useRef<BackGuard | null>(null);

  // 뒤로가기 가드는 편집이 생길 때가 아니라 마운트 때 싣는다 — 켜고 끄기는 ref 로 보고, popstate 는 앱 시작 때 건 리스너(installLeaveGuard)가 라우터보다 먼저 받아 넘긴다.
  useEffect(() => {
    const guard = createBackGuard(windowHistory, {
      token: Math.random().toString(36).slice(2),
      enabled: () => backRef.current,
      message: () => messageRef.current,
      onLeave: () => {
        passRef.current = Date.now();
      },
    });
    guardRef.current = guard;
    if (backRef.current) guard.arm();
    // 앱 시작 때 이미 걸려 있으면 그것을 돌려받는다.
    const off = installLeavePopHub(window as unknown as PopHubHost).add(guard.onPop);
    return () => {
      off();
      guard.dispose();
      guardRef.current = null;
    };
  }, []);

  // 렌더마다 기준 자리를 다시 잡는다 — 라우터가 주소(replace)나 state(서버 액션 뒤)를 갈아 끼워도 기준 주소와 표식이 따라온다.
  useEffect(() => {
    backRef.current = back;
    messageRef.current = message;
    if (back) guardRef.current?.arm();
  });
};
